// Screen 8: Personal Research Wiki — concept-centric knowledge encyclopedia.

import { useAuth } from '../src/lib/auth.jsx';

function WikiScreen() {
  const { t, lang } = useT();
  const navCtx = React.useContext(NavContext);
  const auth = useAuth();
  const [myNotes, setMyNotes] = React.useState(null);
  const [notesError, setNotesError] = React.useState(null);

  React.useEffect(() => {
    if (!auth.user) { setMyNotes(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/notes?limit=50', { credentials: 'same-origin' });
        const body = await res.json();
        if (!cancelled && body?.ok) setMyNotes(body.data?.items ?? []);
      } catch (e) {
        if (!cancelled) setNotesError(e);
      }
    })();
    return () => { cancelled = true; };
  }, [auth.user?.id]);

  return (
    <div className="pp pp-screen" data-screen-label="08 Research wiki">
      <Sidebar active="wiki"/>

      <div className="pp-main">
        <TopBar
          breadcrumbs={<Breadcrumb items={[t('crumb.wiki'), '4D Reconstruction', 'Deformation Field']}/>}
          search="Search your wiki — concepts, methods, equations…"
        />

        <div className="pp-content">
          {/* concept hierarchy tree */}
          <aside className="pp-wiki-tree scroll">
            <div className="section-title" style={{ marginBottom: 8 }}>{t('wk.hierarchy')}</div>
            <WikiTreeRow level={0} label="3D Vision" count="42"/>
            <WikiTreeRow level={1} label="NeRF" count="18"/>
            <WikiTreeRow level={2} label="Compression"/>
            <WikiTreeRow level={2} label="Sparse-view"/>
            <WikiTreeRow level={1} label="Gaussian Splatting" count="22"/>
            <WikiTreeRow level={2} label="3DGS"/>
            <WikiTreeRow level={2} label="4DGS"/>
            <WikiTreeRow level={3} label="Deformation field" current/>
            <WikiTreeRow level={3} label="Canonical frame"/>
            <WikiTreeRow level={3} label="VQ-cache"/>
            <WikiTreeRow level={2} label="Sparse-view 3DGS"/>
            <WikiTreeRow level={1} label="SfM" count="8"/>
            <WikiTreeRow level={0} label="Robotics" count="37"/>
            <WikiTreeRow level={1} label="VLA"/>
            <WikiTreeRow level={2} label="OpenVLA family"/>
            <WikiTreeRow level={2} label="Action tokenization"/>
            <WikiTreeRow level={1} label="Foundation models"/>
            <WikiTreeRow level={1} label="Manipulation"/>

            <div className="pp-wiki-new">
              <I name="plus" size={12}/>
              <span style={{ fontSize: 11.5 }}>{t('wk.new')}</span>
            </div>
          </aside>

          {/* article */}
          <div className="pp-wiki-article scroll">
            {auth.user && myNotes !== null && (
              <section style={{ marginBottom: 32 }}>
                <div className="row gap-2" style={{ alignItems: 'baseline', marginBottom: 12 }}>
                  <h2 className="serif" style={{ fontSize: 22, margin: 0, color: 'var(--fg-0)' }}>
                    {lang === 'ko' ? '내 노트' : 'My notes'}
                  </h2>
                  <span className="chip accent" style={{ height: 18 }}>{myNotes.length}</span>
                </div>
                {myNotes.length === 0 ? (
                  <div style={{ padding: 16, color: 'var(--fg-3)', fontSize: 13 }}>
                    {lang === 'ko'
                      ? '리더에서 텍스트를 선택하고 AI 카드를 만든 뒤 "Save to wiki" 버튼을 누르면 여기에 저장됩니다.'
                      : 'Select text in the reader, generate an AI card, then click "Save to wiki" to file it here.'}
                  </div>
                ) : (
                  <div className="col gap-3">
                    {myNotes.map((n) => (
                      <div key={n.id} className="pp-wiki-note">
                        <div style={{ fontSize: 13.5, color: 'var(--fg-1)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {n.body}
                        </div>
                        <div className="row gap-2" style={{ marginTop: 8, fontSize: 10.5, color: 'var(--fg-3)' }}>
                          <span className="mono">{new Date(n.updated_at * 1000).toLocaleString(lang === 'ko' ? 'ko-KR' : 'en-US')}</span>
                          {n.anchor?.kind && <span className="tag">{n.anchor.kind}</span>}
                          {n.paper_id && <span className="tag indigo">paper</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <hr style={{ borderColor: 'var(--border-1)', margin: '32px 0' }}/>
              </section>
            )}

            <article>
              <div className="row gap-2" style={{ marginBottom: 8 }}>
                <span className="chip ghost mono">3D Vision · 4DGS · {t('wk.kind.concept')}</span>
                <span className="chip accent">14 {t('wk.papers')}</span>
                <span className="chip cyan">6 {t('wk.backlinks')}</span>
              </div>

              <h1 className="serif" style={{
                fontSize: 44, lineHeight: 1.05, letterSpacing: '-0.02em',
                margin: '4px 0 8px', color: 'var(--fg-0)', maxWidth: 720,
              }}>Deformation Field</h1>

              <div className="row gap-3" style={{ alignItems: 'center', color: 'var(--fg-2)', fontSize: 12 }}>
                <span>{t('wk.curated')}</span>
                <span>·</span>
                <span>{t('wk.lastedit')}</span>
                <span>·</span>
                <span style={{ color: 'var(--acc-cyan)' }}>{t('wk.activity.week')}</span>
              </div>

              <div className="pp-wiki-overview">
                <div className="row gap-2" style={{ alignItems: 'baseline', marginBottom: 8 }}>
                  <I name="sparkles" size={12} style={{ color: 'var(--acc-violet)' }}/>
                  <span style={{ fontSize: 11, color: 'var(--acc-violet)', textTransform: 'uppercase', letterSpacing: '0.08em' }} className="mono">{t('wk.aioverview')}</span>
                  <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', marginLeft: 'auto' }}>{t('wk.synth')}</span>
                </div>
                <p style={{ fontSize: 14, color: 'var(--fg-1)', lineHeight: 1.6 }}>
                  {t('wk.overview.p1')}
                </p>
                <p style={{ fontSize: 14, color: 'var(--fg-1)', lineHeight: 1.6, marginTop: 8 }}>
                  {t('wk.overview.p2')}
                </p>
              </div>

              <h2 className="serif pp-wiki-h2">{t('wk.h.notes')}</h2>
              <div className="pp-wiki-note">
                <div style={{ fontSize: 13.5, color: 'var(--fg-1)', lineHeight: 1.6 }}>
                  {t('wk.note.body')}
                </div>
                <div className="row gap-2" style={{ marginTop: 10, fontSize: 10.5, color: 'var(--fg-3)' }}>
                  <span className="mono">{t('wk.edited')}</span>
                  <span>·</span>
                  <span className="tag indigo">rack-twin</span>
                  <span className="tag">design-decision</span>
                </div>
              </div>

              <h2 className="serif pp-wiki-h2">{t('wk.h.formula')}</h2>
              <div className="pp-wiki-eq-block">
                <div className="pp-wiki-eq">
                  x<sub>t</sub> = x<sub>can</sub> + 𝒟<sub>θ</sub>(x<sub>can</sub>, t)
                </div>
                <div style={{ fontSize: 12, color: 'var(--fg-2)', marginTop: 8 }} className="mono">
                  {t('wk.formula.note')}
                </div>
              </div>

              <h2 className="serif pp-wiki-h2">{t('wk.h.cards')}</h2>
              <div className="pp-wiki-cards">
                <WikiCard kind="paper" title="GS-DiT" meta={t('wk.card.primary')}   summary={t('wk.card.gsdit.body')}/>
                <WikiCard kind="paper" title="Deformable-3DGS" meta={t('wk.card.canonical')} summary={t('wk.card.def3dgs.body')}/>
                <WikiCard kind="paper" title="K-Planes" meta={t('wk.card.alt')}     summary={t('wk.card.kplanes.body')}/>
                <WikiCard kind="method" title="VQ canonical-id cache" meta={t('wk.card.impl')}  summary={t('wk.card.vq.body')}/>
                <WikiCard kind="dataset" title="D-NeRF" meta={t('wk.card.dataset')}           summary={t('wk.card.dnerf.body')}/>
                <WikiCard kind="dataset" title="HyperNeRF" meta={t('wk.card.dataset')}        summary={t('wk.card.hyper.body')}/>
              </div>

              <div className="pp-wiki-two-col">
                <div>
                  <h2 className="serif pp-wiki-h2">{t('wk.h.related')}</h2>
                  <div className="pp-wiki-related">
                    {[
                      { y: '26', t: 'GS-DiT', why: 'frozen prior + cache', read: t('wk.read.reading') },
                      { y: '26', t: '4DGS-Cache', why: 'extends cache idea', read: t('wk.read.queue') },
                      { y: '24', t: 'Deformable-3DGS', why: 'canonical MLP form', read: t('wk.read.read') },
                      { y: '24', t: 'Park 24 · def. fields', why: 'foundational math', read: t('wk.read.read') },
                      { y: '23', t: 'K-Planes', why: 'triplane alt.', read: t('wk.read.read') },
                      { y: '23', t: 'TiNeuVox', why: 'voxel-based 𝒟', read: t('wk.read.skim') },
                    ].map(p => (
                      <div key={p.t} className="pp-wiki-rel-row"
                        onClick={() => navCtx.navigate && navCtx.navigate('detail')}
                        style={{ cursor: 'pointer' }}>
                        <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)', width: 24 }}>’{p.y}</span>
                        <span className="grow ellipsis" style={{ fontSize: 12 }}>{p.t}</span>
                        <span style={{ fontSize: 10.5, color: 'var(--fg-2)' }} className="ellipsis">{p.why}</span>
                        <span className={'pp-readstate ' + p.read}>{p.read}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h2 className="serif pp-wiki-h2">{t('wk.h.bench')}</h2>
                  <div className="pp-wiki-bench">
                    <BRow d="D-NeRF" m="PSNR ↑" best="31.74" method="GS-DiT"/>
                    <BRow d="HyperNeRF" m="PSNR ↑" best="31.20" method="4DGS-Cache"/>
                    <BRow d="D-NeRF" m="FPS ↑"  best="120"   method="4DGS-Cache"/>
                    <BRow d="D-NeRF" m="LPIPS ↓" best="0.041" method="GS-DiT"/>
                  </div>

                  <h2 className="serif pp-wiki-h2" style={{ marginTop: 24 }}>{t('wk.h.backlinks')}</h2>
                  <div className="col gap-1">
                    <BacklinkRow icon="book" text="4D Reconstruction (overview)"/>
                    <BacklinkRow icon="book" text="VQ-cache (impl. notes)"/>
                    <BacklinkRow icon="kanban" text="Server-rack digital twin / project"/>
                    <BacklinkRow icon="comment" text="My memo · week 3 repro plan"/>
                    <BacklinkRow icon="formula" text="Eq. 7 · §3.2 GS-DiT"/>
                    <BacklinkRow icon="book" text="Diffusion priors for 3D (concept)"/>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <RailRight id="wiki-toc" defaultWidth={220} min={180} max={340} className="pp-wiki-toc scroll">
            <div className="section-title" style={{ marginBottom: 8 }}>{t('wk.toc.title')}</div>
            <a className="pp-toc-link is-active" href="#">{t('wk.toc.overview')}</a>
            <a className="pp-toc-link" href="#">{t('wk.toc.notes')}</a>
            <a className="pp-toc-link" href="#">{t('wk.toc.formula')}</a>
            <a className="pp-toc-link" href="#">{t('wk.toc.cards')}</a>
            <a className="pp-toc-link" href="#">{t('wk.toc.related')}</a>
            <a className="pp-toc-link" href="#">{t('wk.toc.bench')}</a>
            <a className="pp-toc-link" href="#">{t('wk.toc.backlinks')}</a>

            <div className="section-title" style={{ marginTop: 20, marginBottom: 8 }}>{t('wk.activity')}</div>
            <ActivityRow when="2h" text={t('wk.act.r1')}/>
            <ActivityRow when="1d" text={t('wk.act.r2')}/>
            <ActivityRow when="3d" text={t('wk.act.r3')}/>
            <ActivityRow when="6d" text={t('wk.act.r4')}/>
          </RailRight>
        </div>
      </div>
    </div>
  );
}

function WikiTreeRow({ level, label, count, current }) {
  return (
    <div className={'pp-wiki-tree-row' + (current ? ' is-current' : '')} style={{ paddingLeft: 8 + level * 14 }}>
      <span className="grow ellipsis" style={{ fontSize: 12, color: current ? 'var(--fg-0)' : 'var(--fg-1)' }}>{label}</span>
      {count && <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{count}</span>}
    </div>
  );
}

function WikiCard({ kind, title, meta, summary }) {
  const kindColor = kind === 'paper' ? 'var(--acc-indigo)' : kind === 'method' ? 'var(--acc-cyan)' : 'var(--acc-violet)';
  const kindIcon = kind === 'paper' ? 'figure' : kind === 'method' ? 'code' : 'data';
  return (
    <div className="pp-wiki-card">
      <div className="row gap-2" style={{ alignItems: 'baseline' }}>
        <div style={{ width: 18, height: 18, borderRadius: 4, background: `${kindColor}22`, color: kindColor, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${kindColor}44` }}>
          <I name={kindIcon} size={11}/>
        </div>
        <span style={{ fontSize: 11, color: kindColor, letterSpacing: '0.06em', textTransform: 'uppercase' }} className="mono">{kind}</span>
      </div>
      <div className="serif" style={{ fontSize: 15, marginTop: 6 }}>{title}</div>
      <div style={{ fontSize: 11, color: 'var(--fg-3)' }} className="mono">{meta}</div>
      <div style={{ fontSize: 12, color: 'var(--fg-1)', marginTop: 8, lineHeight: 1.5 }}>{summary}</div>
    </div>
  );
}

function BRow({ d, m, best, method }) {
  return (
    <div className="row gap-3" style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-0)', alignItems: 'baseline' }}>
      <span className="mono ellipsis" style={{ fontSize: 11, color: 'var(--fg-3)', width: 88 }}>{d}</span>
      <span className="mono" style={{ fontSize: 11, color: 'var(--fg-2)', width: 70 }}>{m}</span>
      <span className="mono" style={{ fontSize: 13, color: 'var(--acc-indigo)', width: 60, textAlign: 'right' }}>{best}</span>
      <span className="grow ellipsis" style={{ fontSize: 11.5, color: 'var(--fg-1)' }}>{method}</span>
    </div>
  );
}

function BacklinkRow({ icon, text }) {
  return (
    <a className="pp-backlink-row" href="#">
      <I name={icon} size={11} style={{ color: 'var(--fg-3)' }}/>
      <span className="ellipsis" style={{ fontSize: 12 }}>{text}</span>
    </a>
  );
}

function ActivityRow({ when, text }) {
  return (
    <div className="row gap-2" style={{ padding: '5px 0', alignItems: 'flex-start' }}>
      <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', width: 20, paddingTop: 1 }}>{when}</span>
      <span style={{ fontSize: 11.5, color: 'var(--fg-1)', flex: 1 }}>{text}</span>
    </div>
  );
}

if (!document.getElementById('pp-wiki-styles')) {
  const s = document.createElement('style');
  s.id = 'pp-wiki-styles';
  s.textContent = `
  .pp-wiki-tree { width: 220px; flex-shrink: 0; border-right: 1px solid var(--border-1); background: var(--bg-1); padding: 18px 8px; overflow: auto; }
  .pp-wiki-tree-row { display: flex; align-items: center; gap: 6px; padding: 5px 8px; border-radius: 5px; color: var(--fg-1); cursor: pointer; }
  .pp-wiki-tree-row:hover { background: rgba(255,255,255,0.025); }
  .pp-wiki-tree-row.is-current { background: rgba(136,150,255,0.12); box-shadow: inset 2px 0 0 var(--acc-indigo); }
  .pp-wiki-new { display: flex; align-items: center; gap: 6px; padding: 8px; margin-top: 12px; border: 1px dashed var(--border-2); border-radius: 6px; color: var(--fg-3); cursor: pointer; justify-content: center; }
  .pp-wiki-new:hover { color: var(--fg-0); border-color: var(--border-3); }

  .pp-wiki-article {
    flex: 1; min-width: 0;
    padding: 32px 48px 48px;
    overflow: auto;
    max-width: 100%;
  }
  .pp-wiki-article article { max-width: 820px; }
  .pp-wiki-h2 { font-size: 22px; letter-spacing: -0.01em; color: var(--fg-0); margin: 28px 0 12px; font-weight: 400; }
  .pp-wiki-overview {
    margin-top: 22px;
    padding: 16px 18px;
    border: 1px solid var(--border-1);
    border-radius: 12px;
    background: linear-gradient(180deg, rgba(180,155,255,0.04), transparent 60%);
  }
  .pp-wiki-link { color: var(--acc-indigo); text-decoration: none; border-bottom: 1px dashed rgba(136,150,255,0.40); }
  .pp-code-inl { font-size: 12.5px; padding: 1px 5px; border-radius: 3px; background: rgba(255,255,255,0.05); color: var(--acc-cyan); }
  .pp-wiki-note {
    margin: 4px 0 4px;
    padding: 14px 16px;
    border-left: 3px solid var(--warn);
    background: linear-gradient(90deg, rgba(232,184,106,0.06), transparent);
    border-radius: 0 8px 8px 0;
  }
  .pp-wiki-eq-block { padding: 14px 18px; background: var(--bg-2); border: 1px solid var(--border-1); border-radius: 8px; }
  .pp-wiki-eq { font-family: 'Cambria Math', serif; font-style: italic; font-size: 19px; color: var(--fg-0); text-align: center; }

  .pp-wiki-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .pp-wiki-card { background: var(--bg-2); border: 1px solid var(--border-1); border-radius: 10px; padding: 12px; }

  .pp-wiki-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 28px; margin-top: 12px; }
  .pp-wiki-related { background: var(--bg-2); border: 1px solid var(--border-1); border-radius: 10px; overflow: hidden; }
  .pp-wiki-rel-row { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-bottom: 1px solid var(--border-0); }
  .pp-wiki-rel-row:last-child { border-bottom: 0; }
  .pp-readstate { font-family: var(--font-mono); font-size: 9.5px; padding: 1px 6px; border-radius: 999px; text-transform: uppercase; letter-spacing: 0.04em; }
  .pp-readstate.read { color: var(--ok); background: var(--ok-bg); }
  .pp-readstate.reading { color: var(--acc-indigo); background: rgba(136,150,255,0.10); }
  .pp-readstate.queue { color: var(--acc-cyan); background: var(--info-bg); }
  .pp-readstate.skim { color: var(--fg-3); background: rgba(255,255,255,0.04); }
  .pp-wiki-bench { background: var(--bg-2); border: 1px solid var(--border-1); border-radius: 10px; overflow: hidden; }
  .pp-backlink-row { display: flex; align-items: center; gap: 6px; padding: 4px 6px; color: var(--fg-1); text-decoration: none; border-radius: 5px; font-size: 11.5px; }
  .pp-backlink-row:hover { background: rgba(255,255,255,0.025); }

  .pp-wiki-toc { width: 220px; flex-shrink: 0; border-left: 1px solid var(--border-1); background: var(--bg-1); padding: 32px 14px; overflow: auto; }
  .pp-toc-link { display: block; padding: 5px 8px; border-radius: 4px; color: var(--fg-2); font-size: 11.5px; text-decoration: none; }
  .pp-toc-link:hover { color: var(--fg-0); }
  .pp-toc-link.is-active { color: var(--fg-0); box-shadow: inset 2px 0 0 var(--acc-indigo); background: rgba(136,150,255,0.06); }
  `;
  document.head.appendChild(s);
}

export { WikiScreen };
